'use client';

import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import Image from 'next/image';

import 'swiper/css';
import { BiUser } from 'react-icons/bi';
import { useTranslation } from '@/hooks/useTranslation';

type Props = { locale: string };

const SectionTrustedBy = ({ locale }: Props) => {
  const [sw, setSw] = useState<any | null>(null);
  const { t } = useTranslation(locale);

  // Logos des partenaires/technologies
  const partnerLogos = [
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911043799_Google.svg',
      alt: 'Google',
      width: 120,
    },
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911043746_Microsoft.svg',
      alt: 'Microsoft',
      width: 120,
    },
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911043710_HP.svg',
      alt: 'HP',
      width: 80,
    },
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911043713_MyPIL.svg',
      alt: 'MyPIL',
      width: 100,
    },
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911043715_Nvidia.svg',
      alt: 'Nvidia',
      width: 120,
    },
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911043725_OpenAI.svg',
      alt: 'OpenAI',
      width: 120,
    },
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/2404240721339_anthropic-logotype.svg',
      alt: 'Anthropic',
      width: 120,
    },
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911043761_SUM.svg',
      alt: 'SUM',
      width: 100,
    },
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/24010911043776_Zapier.svg',
      alt: 'Zapier',
      width: 120,
    },
    {
      src: 'https://cdn.1cdn.app/application/LOUISEBOT/24011712163687_amazon.svg',
      alt: 'Amazon',
      width: 120,
    },
  ];

  // Duplicate for infinite scroll effect
  const infiniteLogos = [...partnerLogos, ...partnerLogos, ...partnerLogos];

  useEffect(() => {
    sw?.update();
  }, [sw]);

  return (
    <section className="tw-py-16 lg:tw-py-20  tw-bg-transparent>">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* Stats Section */}
        <div className="tw-mb-16">
          <div className="tw-text-center tw-mb-12">
            <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
              <BiUser className="tw-w-4 tw-h-4" />
              {t('Home/SectionTrustBy/badge')}
            </div>
            <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-font-['Lexend']">
              {t('Home/SectionTrustBy/key1')}
            </h2>
            <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-font-['Lexend']">
              {t('Home/SectionTrustBy/description')}
            </p>
          </div>

          {/* Trust Indicators */}
          <div className="tw-grid sm:tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-8 tw-mb-16">
            <div className="tw-text-center tw-p-6 tw-bg-white tw-rounded-2xl tw-shadow-lg tw-border tw-border-gray-100">
              <div className="tw-text-3xl tw-font-bold tw-text-blue-600 tw-mb-2 tw-font-['Lexend']">
                {t('Home/SectionTrustBy/stat1-number')}
              </div>
              <div className="tw-text-gray-600 tw-font-medium tw-font-['Lexend']">
                {t('Home/SectionTrustBy/stat1-label')}
              </div>
            </div>

            <div className="tw-text-center tw-p-6 tw-bg-white tw-rounded-2xl tw-shadow-lg tw-border tw-border-gray-100">
              <div className="tw-text-3xl tw-font-bold tw-text-yellow-500 tw-mb-2 tw-font-['Lexend']">
                {t('Home/SectionTrustBy/stat2-number')}
              </div>

              <div className="tw-text-gray-600 tw-font-medium tw-font-['Lexend']">
                {t('Home/SectionTrustBy/stat2-label')}
              </div>
            </div>

            <div className="tw-text-center tw-p-6 tw-bg-white tw-rounded-2xl tw-shadow-lg tw-border tw-border-gray-100">
              <div className="tw-text-3xl tw-font-bold tw-text-green-600 tw-mb-2 tw-font-['Lexend']">
                {t('Home/SectionTrustBy/stat3-number')}
              </div>
              <div className="tw-text-gray-600 tw-font-medium tw-font-['Lexend']">
                {t('Home/SectionTrustBy/stat3-label')}
              </div>
            </div>
          </div>
        </div>

        {/* Partners Section */}
        <div className="tw-mb-8">
          <h3 className="tw-text-center tw-text-lg tw-font-semibold tw-text-gray-500 tw-mb-8 tw-font-['Lexend'] tw-uppercase tw-tracking-wider">
            {t('Home/SectionTrustBy/partners-title')}
          </h3>
        </div>

        {/* Logo Carousel */}
        <div className="tw-relative tw-overflow-hidden">
          {/* Gradient overlays */}
          <div className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-20 tw-bg-gradient-to-r tw-from-gray-50 tw-to-transparent tw-z-10"></div>
          <div className="tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-20 tw-bg-gradient-to-l tw-from-gray-50 tw-to-transparent tw-z-10"></div>

          <Swiper
            spaceBetween={40}
            loop={true}
            autoplay={{
              delay: 0,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            speed={3000}
            className="tw-py-8"
            onResize={(swiper) => swiper.update()}
            modules={[Autoplay]}
            slidesPerView={'auto'}
            onSwiper={setSw}
            breakpoints={{
              320: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              640: {
                slidesPerView: 3,
                spaceBetween: 30,
              },
              768: {
                slidesPerView: 4,
                spaceBetween: 40,
              },
              1024: {
                slidesPerView: 5,
                spaceBetween: 50,
              },
              1280: {
                slidesPerView: 6,
                spaceBetween: 60,
              },
            }}
          >
            {infiniteLogos.map((logo, index) => (
              <SwiperSlide key={`${logo.alt}-${index}`} className="tw-flex tw-items-center tw-justify-center">
                <div className="tw-flex tw-items-center tw-justify-center tw-h-16 tw-px-4 tw-transition-all tw-duration-300 hover:tw-scale-110 tw-cursor-pointer tw-bg-white tw-rounded-xl tw-shadow-sm hover:tw-shadow-md tw-border tw-border-gray-100">
                  <Image
                    priority
                    quality={100}
                    src={logo.src}
                    alt={`${logo.alt} logo - Trusted partner`}
                    width={logo.width}
                    height={40}
                    className="tw-max-h-8 tw-w-auto tw-object-contain tw-filter tw-grayscale hover:tw-grayscale-0 tw-transition-all tw-duration-300"
                    style={{
                      maxWidth: `${logo.width}px`,
                      height: 'auto',
                    }}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default SectionTrustedBy;
